/**
 * use-contacts.ts — CRUD hooks pour la table `immo_contacts`
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTeamId } from "@/hooks/use-team-id";
import { useToast } from "@/hooks/use-toast";
import { invalidateEnrollmentQueries } from "@/hooks/use-campaign-enrollments";
import type { Database } from "@/integrations/supabase/types";

type ContactRow = Database["public"]["Tables"]["immo_contacts"]["Row"];
type ContactInsert = Database["public"]["Tables"]["immo_contacts"]["Insert"];
type ContactUpdate = Database["public"]["Tables"]["immo_contacts"]["Update"];

export type { ContactRow };

export function useContacts(search?: string) {
  const { teamId } = useTeamId();

  return useQuery({
    queryKey: ["contacts", teamId, search],
    queryFn: async () => {
      let query = supabase
        .from("immo_contacts")
        .select("*")
        .eq("team_id", teamId!)
        .order("created_at", { ascending: false })
        .limit(500);

      if (search && search.trim().length > 0) {
        const term = `%${search.trim()}%`;
        // Recherche elargie : nom/contact + ville + projet + souvenir + tags
        query = query.or(
          `name.ilike.${term},email.ilike.${term},phone.ilike.${term},city.ilike.${term},project_city.ilike.${term},agent_memory.ilike.${term},tags.cs.{${search.trim()}}`
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as ContactRow[];
    },
    enabled: !!teamId,
  });
}

export function useCreateContact() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: Omit<ContactInsert, "team_id">) => {
      const { data, error } = await supabase
        .from("immo_contacts")
        .insert({ ...input, team_id: teamId! })
        .select("*")
        .single();
      if (error) throw error;
      return data as ContactRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts", teamId] });
      queryClient.invalidateQueries({ queryKey: ["crm-pipeline", teamId] });
      toast({ title: "Contact créé" });
    },
    onError: (err: unknown) => {
      // 23505 = violation de la contrainte d'unicite (team_id, email) :
      // un contact avec cet email existe deja. On le dit clairement plutot
      // qu'un generique muet (cf. use-terrain-submit.ts).
      const pgErr = err as { code?: string };
      const description =
        pgErr.code === "23505"
          ? "Un contact avec cet email existe déjà dans ton CRM."
          : "Impossible de créer le contact.";
      toast({
        title: "Erreur",
        description,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateContact() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: ContactUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from("immo_contacts")
        .update(updates)
        .eq("id", id)
        .eq("team_id", teamId!)
        .select("*")
        .single();
      if (error) throw error;
      return data as ContactRow;
    },
    // Optimistic update : la table CRM et le drawer voient la nouvelle valeur
    // instantanement, sans attendre le retour serveur.
    onMutate: async ({ id, ...updates }) => {
      await queryClient.cancelQueries({ queryKey: ["contacts", teamId] });
      await queryClient.cancelQueries({ queryKey: ["crm-pipeline", teamId] });

      const previousContacts = queryClient.getQueriesData<ContactRow[]>({
        queryKey: ["contacts", teamId],
      });
      const previousPipeline = queryClient.getQueriesData<unknown>({
        queryKey: ["crm-pipeline", teamId],
      });

      queryClient.setQueriesData<ContactRow[]>(
        { queryKey: ["contacts", teamId] },
        (old) => {
          if (!old) return old;
          return old.map((c) => (c.id === id ? ({ ...c, ...updates } as ContactRow) : c));
        }
      );

      return { previousContacts, previousPipeline };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousContacts) {
        for (const [key, data] of context.previousContacts) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousPipeline) {
        for (const [key, data] of context.previousPipeline) {
          queryClient.setQueryData(key, data);
        }
      }
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le contact.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      // Resync avec le serveur en arriere-plan
      queryClient.invalidateQueries({ queryKey: ["contacts", teamId] });
      queryClient.invalidateQueries({ queryKey: ["contact-details"] });
      queryClient.invalidateQueries({ queryKey: ["crm-pipeline", teamId] });
    },
  });
}

export function useDeleteContact() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("immo_contacts")
        .delete()
        .eq("id", id)
        .eq("team_id", teamId!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts", teamId] });
      queryClient.invalidateQueries({ queryKey: ["contact-details"] });
      queryClient.invalidateQueries({ queryKey: ["crm-pipeline", teamId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["unified-messages"] });
      queryClient.invalidateQueries({ queryKey: ["conversation-messages"] });
      invalidateEnrollmentQueries(queryClient, teamId);
      toast({ title: "Contact supprimé" });
    },
    onError: () => {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le contact.",
        variant: "destructive",
      });
    },
  });
}
